import { CUSTOM_ROUTINE_TRIGGER, DaysOfWeek } from 'enums';
import { ACTIVITY_IMAGE_ALLOWED_EXTENSIONS } from 'constants/image';
import {
  ACTIVITY_TYPES,
  MIN_PASSWORD_LENGTH,
  PRIORITY_OPTION,
  STRIPE_CUSTOMER_ID_CHARACTER,
  TIME_FORMAT_24H,
  BREAK_FREQUENCY_MINUTES,
  EMPTY_STRING
} from 'constants/general';
import { NewTaskFormValues, ValidationErrorNewTask } from 'interfaces';
import { ActivityType } from 'interfaces/common.interface';
import {
  CustomRoutineType,
  RoutineTimeValidation,
  SettingsType
} from 'interfaces/setting.interface';
import isEqual from 'fast-deep-equal';
import moment from 'moment';
import { t } from 'services/i18n.service';
import { omitKeys } from 'utils/helpers/omitKeys';
import { createHtml } from 'utils/helpers';
import { formatTimeTo12Hour } from 'utils/time.util';
import { NODE_ENV } from 'constants/node-env';

export * from './timeInputValidation';

export const validateNewToDoTask = (values: NewTaskFormValues) => {
  const errors: ValidationErrorNewTask = {};

  if (!values.title) {
    errors.title = '*';
  }
  if (!values.due_date) {
    errors.due_date = '*';
  }
  if (!values.eisenhower_quadrant.value) {
    errors.eisenhower_quadrant = '*';
  }
  return errors;
};

export const validateInputDuration = (
  value: string,
  maxValue: number,
  type?: string
) => {
  if (parseFloat(value) < 0) {
    return { error: true, data: -1 };
  }

  if (type === ACTIVITY_TYPES.BREAKING) {
    return parseInt(value) > maxValue
      ? { error: true, data: -1 }
      : { error: false, data: Math.abs(parseInt(value)) };
  } else {
    return parseFloat(value) > maxValue
      ? { error: true, data: -1 }
      : {
          error: false,
          data: Math.abs(parseInt(value))
        };
  }
};

export const validateUrl = (value: string) =>
  /(http|https)?:\/\/([\w\d-]+\.)+\w{2,}(\/.+)?$/.test(value);

export const normalizeAllowedWebsiteUrl = (value: string): string =>
  value.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');

export const isValidAllowedWebsiteUrl = (value: string): boolean => {
  const normalized = normalizeAllowedWebsiteUrl(value);
  if (normalized.length === 0) {
    return false;
  }
  return validateUrl(`https://${normalized}`);
};

export const isDarkMode = () =>
  document.documentElement.classList.contains('dark');

export const isAppInProduction = () =>
  process.env.NODE_ENV === NODE_ENV.PRODUCTION;

export const isStripeCustomerId = (arg: string) =>
  /^cus_[a-zA-Z0-9]+/i.test(arg) && arg.length === STRIPE_CUSTOMER_ID_CHARACTER;

export const hasCompletionRequirement = (habit?: ActivityType) =>
  habit &&
  'completion_requirements' in habit &&
  habit.completion_requirements !== undefined &&
  habit.completion_requirements !== null;

export const isValidImageURL = (url: string) => {
  if (typeof url !== 'string') {
    return false;
  }

  const allowedExtensions = ACTIVITY_IMAGE_ALLOWED_EXTENSIONS.join('|');

  return new RegExp(
    `^https?:\\/\\/.+\\.(${allowedExtensions})(?:\\?.*)?(?:#.*)?$`,
    'i'
  ).test(url.trim());
};

export const isCurrentPlayingActivityFromChoice = (
  activities: ActivityType[],
  currentPlayingActivity: ActivityType
) => {
  let result: {
    parent_activity_id: string | null;
    choice_id: string | null;
  } = { parent_activity_id: null, choice_id: null };
  // change so that all sequence types can have choices
  activities?.forEach((activity: ActivityType) => {
    const choice = activity?.choices?.find(
      (choice) => choice.id === currentPlayingActivity?.id
    );
    if (choice) {
      result = {
        parent_activity_id: activity.id,
        choice_id: choice.id
      };
    } else if (activity.id === currentPlayingActivity?.id) {
      result = {
        parent_activity_id: activity.id,
        choice_id: null
      };
    }
  });
  return result;
};

export const isActivityFilterByAll = (
  activity: ActivityType,
  searchWord: string,
  priority: string,
  daysOfWeek?: string[]
) => {
  const isSubStringFound =
    searchWord === ''
      ? true
      : String(activity?.name ?? '')
          .toLowerCase()
          .includes(searchWord.toLowerCase());
  const isPrioritized =
    priority === ''
      ? true
      : (activity?.priority ?? PRIORITY_OPTION.STANDARD) === priority;
  const selectedDays =
    (activity.days_of_week as DaysOfWeek[])?.filter((day) =>
      daysOfWeek?.includes(day)
    ) ?? [];
  const areSelectedDaysExists =
    daysOfWeek && daysOfWeek.length > 0 ? selectedDays.length > 0 : true;

  return isSubStringFound && isPrioritized && areSelectedDaysExists;
};

/**
 * @returns boolean by comparing the current user settings
 * with the previous/existing one(i.e the previous settings
 * might come from mac,win ... apps)
 */
export const compareSettings = (
  currentSettings: SettingsType,
  previousSettings: SettingsType
) => {
  let has_edited_settings = false;
  const currentSetting: any = { ...currentSettings }; // eslint-disable-line @typescript-eslint/no-explicit-any
  const previousSetting: any = { ...previousSettings }; // eslint-disable-line @typescript-eslint/no-explicit-any

  const allKeys = new Set([
    ...Object.keys(previousSetting),
    ...Object.keys(currentSetting)
  ]);

  for (const key of allKeys) {
    if (
      key === ACTIVITY_TYPES.MORNING ||
      key === ACTIVITY_TYPES.EVENING ||
      key === ACTIVITY_TYPES.BREAKING ||
      key === ACTIVITY_TYPES.STANDALONE
    ) {
      const currentArray = currentSetting[key] ?? [];
      const previousArray = previousSetting[key] ?? [];

      if (currentArray.length !== previousArray.length) {
        has_edited_settings = true;
        break;
      } else {
        for (const subKey in previousArray) {
          const previous_setting_child = omitKeys(previousArray[subKey], [
            'id',
            'activity_sequence_id'
          ]);
          const current_setting_child = omitKeys(currentArray[subKey], [
            'id',
            'activity_sequence_id'
          ]);
          if (!isEqual(previous_setting_child, current_setting_child)) {
            has_edited_settings = true;
            break;
          } else {
            has_edited_settings = false;
          }
        }
        if (has_edited_settings) {
          break;
        }
      }
    } else {
      if (!isEqual(previousSetting[key], currentSetting[key])) {
        has_edited_settings = true;
        break;
      }
    }
  }
  return has_edited_settings;
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

export const isYoutubeURL = (url: string) =>
  !!url.match(
    /^(?:(?:https?:)?\/\/)?(?:www\.)?(?:m\.)?(?:youtu(?:be)?\.com\/(?:v\/|embed\/|shorts\/|watch(?:\/|\?v=))|youtu\.be\/)((?:\w|-){11})(?:\S+)?$/
  );

export const isVimeoURL = (url: string) =>
  !!url.match(
    /^(?:(?:https?:)?\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/)?([0-9]{8,12})(?:$|[?/].*)/
  );

export const isValidUrl = (urlString: string) => {
  try {
    return Boolean(new URL(urlString));
  } catch {
    return false;
  }
};

export const validateCustomRoutineTimeIntervals = (
  customRoutine?: CustomRoutineType
) => {
  const isInvalidTime: {
    isInvalidStartTime: boolean;
    isInvalidEndTime: boolean;
    errors: { startTime: string[]; endTime: string[] };
  } = {
    isInvalidStartTime: false,
    isInvalidEndTime: false,
    errors: { startTime: [], endTime: [] }
  };

  if (!isValidTime(customRoutine?.start_time)) {
    isInvalidTime.isInvalidStartTime = true;
    isInvalidTime.errors.startTime = [t('errors.empty_time')];
  } else {
    isInvalidTime.isInvalidStartTime = moment(
      customRoutine?.start_time,
      TIME_FORMAT_24H
    ).isSameOrAfter(moment(customRoutine?.end_time, TIME_FORMAT_24H), 'minute');
    if (isInvalidTime.isInvalidStartTime) {
      isInvalidTime.errors.startTime = [t('validation.invalid_time')];
    }
  }

  if (!isValidTime(customRoutine?.end_time)) {
    isInvalidTime.isInvalidEndTime = true;
    isInvalidTime.errors.endTime = [t('errors.empty_time')];
  } else {
    isInvalidTime.isInvalidEndTime = moment(
      customRoutine?.end_time,
      TIME_FORMAT_24H
    ).isSameOrBefore(
      moment(customRoutine?.start_time, TIME_FORMAT_24H),
      'minute'
    );
    if (isInvalidTime.isInvalidEndTime) {
      isInvalidTime.errors.endTime = [t('validation.invalid_time')];
    }
  }

  return isInvalidTime;
};

export const hasInvalidCustomRoutineSchedules = (
  routines?: CustomRoutineType[]
) => {
  return !routines?.length
    ? false
    : routines?.some((routine) => {
        let isInvalidRoutine = false;
        if (routine.trigger === CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE) {
          const isInvalidStartTime = moment(
            routine?.start_time,
            TIME_FORMAT_24H
          ).isSameOrAfter(moment(routine?.end_time, TIME_FORMAT_24H), 'minute');
          const isInvalidEndTime = moment(
            routine?.end_time,
            TIME_FORMAT_24H
          ).isSameOrBefore(
            moment(routine?.start_time, TIME_FORMAT_24H),
            'minute'
          );
          isInvalidRoutine =
            !routine.start_time ||
            !routine.end_time ||
            isInvalidStartTime ||
            isInvalidEndTime ||
            !routine.days_of_week?.length;
        }
        return !routine.name || isInvalidRoutine;
      });
};

const isValidTime = (time?: string) => {
  return moment(time, TIME_FORMAT_24H, true).isValid();
};

export const hasMeaningfulCutoff = (cutoffTime?: string | null): boolean => {
  return typeof cutoffTime === 'string' && cutoffTime.trim() !== EMPTY_STRING;
};

export const shouldValidateActivityCutoff = (
  activity?: Pick<ActivityType, 'priority'>
): boolean => {
  return (activity?.priority ?? PRIORITY_OPTION.HIGH) === PRIORITY_OPTION.HIGH;
};

export const isLateCutoffRequired = (
  activity?: Pick<ActivityType, 'priority' | 'isLateCutoffEnabled'>
): boolean =>
  shouldValidateActivityCutoff(activity) &&
  Boolean(activity?.isLateCutoffEnabled);

type ActivityCutoffValidationResult = {
  isValid: boolean;
  message: string | null;
};

/**
 * Per-habit late cutoff is intentionally independent of the global cutoff,
 * startup time, and shutdown time (backend removed the matching server guard).
 * Only validates that a set cutoff is a usable HH:mm time of day.
 */
export const getActivityCutoffTimeValidation = (
  activityCutoffTime?: string
): ActivityCutoffValidationResult => {
  if (!hasMeaningfulCutoff(activityCutoffTime)) {
    return { isValid: true, message: null };
  }

  return isValidTime(activityCutoffTime)
    ? { isValid: true, message: null }
    : { isValid: false, message: t('validation.invalid_time_message') };
};

export const isActivityCutoffTimeValid = (
  cutoff_time_for_doing_activity?: string
) => getActivityCutoffTimeValidation(cutoff_time_for_doing_activity).isValid;

export const validateRoutineTimes = (
  startupTime?: string,
  shutdownTime?: string,
  cutoffTime?: string
): RoutineTimeValidation => {
  const validationResult: RoutineTimeValidation = {
    isValid: {
      startupTime: true,
      shutdownTime: true,
      cutoffTime: true
    },
    errorMessages: {
      startupTime: [],
      shutdownTime: [],
      cutoffTime: []
    }
  };

  const startupMoment = moment(startupTime, TIME_FORMAT_24H, true);
  const shutdownMoment = moment(shutdownTime, TIME_FORMAT_24H, true);
  const isStartupValid = startupMoment.isValid();
  const isShutdownValid = shutdownMoment.isValid();

  if (!isStartupValid) {
    validationResult.isValid.startupTime = false;
    validationResult.errorMessages.startupTime.push(
      t('validation.invalid_time_message')
    );
  }

  if (!isShutdownValid) {
    validationResult.isValid.shutdownTime = false;
    validationResult.errorMessages.shutdownTime.push(
      t('validation.invalid_time_message')
    );
  }

  // Check if startup and shutdown times are equal
  if (isStartupValid && isShutdownValid) {
    if (startupMoment.isSame(shutdownMoment, 'minute')) {
      validationResult.isValid.startupTime = false;
      validationResult.isValid.shutdownTime = false;
      validationResult.errorMessages.startupTime.push(
        t('validation.startup_shutdown_cannot_be_equal')
      );
      validationResult.errorMessages.shutdownTime.push(
        t('validation.shutdown_startup_cannot_be_equal')
      );
    }
  }

  if (cutoffTime) {
    const cutoffMoment = moment(cutoffTime, TIME_FORMAT_24H, true);
    const isCutoffValid = cutoffMoment.isValid();

    if (!isCutoffValid) {
      validationResult.isValid.cutoffTime = false;
      validationResult.errorMessages.cutoffTime.push(
        t('validation.invalid_time_message')
      );
    } else if (isStartupValid && isShutdownValid) {
      const isNightShift = startupMoment.isAfter(shutdownMoment);
      const isCutoffInShiftInvalidRange = isNightShift
        ? cutoffMoment.isAfter(startupMoment) ||
          cutoffMoment.isBefore(shutdownMoment)
        : cutoffMoment.isAfter(startupMoment) &&
          cutoffMoment.isBefore(shutdownMoment);

      if (isCutoffInShiftInvalidRange) {
        validationResult.isValid.cutoffTime = false;
        validationResult.errorMessages.cutoffTime.push(
          t('errors.sleep_time_must_be_after_finish_work_and_before_wake_up', {
            finish_work_time: formatTimeTo12Hour(shutdownTime),
            wake_up_time: formatTimeTo12Hour(startupTime)
          })
        );
      }
    }
  }

  return validationResult;
};

export const getPasswordConditions = (password: string) => {
  const hasMinLength = password.length >= MIN_PASSWORD_LENGTH;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[^A-Za-z0-9\s]/.test(password);

  const rulesPassed = [
    hasLowercase,
    hasUppercase,
    hasNumber,
    hasSpecialChar
  ].filter(Boolean).length;
  const hasAtLeastThree = rulesPassed >= 3;

  return [
    {
      title: t('auth_page.at_least_num_chars_characters', {
        num_chars: MIN_PASSWORD_LENGTH
      }),
      valid: hasMinLength,
      subConditions: []
    },
    {
      title: t('auth_page.at_least_num_rules_of_the_following', {
        num_rules: 3
      }),
      valid: hasAtLeastThree,
      subConditions: [
        {
          title: t('auth_page.lower_case_letters_a_z'),
          valid: hasLowercase
        },
        {
          title: t('auth_page.upper_case_letters_a_z'),
          valid: hasUppercase
        },
        {
          title: t('auth_page.numbers_zero_nine'),
          valid: hasNumber
        },
        {
          title: t('auth_page.special_characters'),
          valid: hasSpecialChar
        }
      ]
    }
  ];
};

export const isTagValid = (tag: string) =>
  !tag || /^[A-Za-z][A-Za-z_ (),&-]*$/.test(tag);

export const breakFrequencyValidation = (breakFrequency?: number) => {
  let message = t('minutes');
  let isValid = true;

  if (breakFrequency === undefined || breakFrequency === null) {
    isValid = false;
    message = t('minutes');
  } else if (breakFrequency < BREAK_FREQUENCY_MINUTES.MIN) {
    isValid = false;
    message = t('errors.break_time');
  } else if (breakFrequency > BREAK_FREQUENCY_MINUTES.MAX) {
    isValid = false;
    message = t('errors.break_time_exceed');
  }

  return { message, isValid };
};

export const isInteger = (value: unknown) =>
  typeof value === 'number' && isFinite(value) && Math.floor(value) === value;

export const isString = (value: unknown) =>
  typeof value === 'string' || value instanceof String;

export const isValidHabitName = (name: string): boolean => {
  if (typeof name !== 'string' || !name.trim()) {
    return false;
  }

  // Guards:
  // - Disallow HTML entity-like sequences (&...;) to reduce XSS risks while still allowing a bare '&'
  // - Disallow control and format characters (Cc, Cf) to prevent obfuscated payloads
  // - Require at least one Unicode letter
  // Allowed punctuation: . , ! ? ' " ( ) [ ] - – — & / : _ + % = # @ *
  const entityGuard =
    /(?!.*&(?:#\d+|#[xX][0-9A-Fa-f]+|[A-Za-z][A-Za-z0-9]{1,31});)/;
  const controlGuard = /(?!.*[\p{Cc}\p{Cf}])/u;
  // Allow letters/digits/space/common punctuation plus square brackets via alternation
  const validChars =
    '(?=.*\\p{L})(?:[\\p{L}\\p{N}\\s.,!?\'"]|[()_\\-+&/:–—’%=#@*]|[\\[\\]])+';
  const validPattern = new RegExp(
    `^${entityGuard.source}${controlGuard.source}${validChars}$`,
    'u'
  );

  const value = name.trim();
  return validPattern.test(value);
};

export const decodeHtmlEntities = (text: string): string => {
  const result = createHtml(text ?? '');
  return typeof result === 'string' ? result : '';
};
